import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import { BooleanSwitchValue } from "consts/general";
import { getFormInputProps } from "util/forms";
import { TextFieldLoadable } from "ui/atoms/TextField";
import { InputableRadioGroup, OnboardingInputControl } from "ui/molecules/Input";
import OnboardingForm from "../../OnboardingForm";
import { FormField, type OnboardingMarketingWebsiteAndSupportProps } from "./types";
import RefundChargebackPolicyFileUploader from "./RefundChargebackPolicyFileUploader";

const MarketingWebsiteAndSupport = (props: OnboardingMarketingWebsiteAndSupportProps) => {
    const {
        values,
        isSubmitting,
        isValid,
        isBootstrapDataLoading,
        isIncomplete,
        requestedChangeFields,
        bootstrapData,
        setFieldValue,
        setFieldTouched,
        handleChange,
        handleBlur,
        handleSubmit,
        ...form
    } = props;

    return (
        <OnboardingForm
            {...form}
            isSubmitting={isSubmitting}
            isValid={isValid}
            isIncomplete={isIncomplete}
            requestedChangeFields={requestedChangeFields}
            handleSubmit={handleSubmit}
        >
            <OnboardingInputControl
                name={FormField.HasCreditLimitPolicy}
                isIncomplete={isIncomplete}
                requestedChangeFields={requestedChangeFields}
                renderInput={({ disabled }) => (
                    <InputableRadioGroup
                        slotProps={{
                            typography: {
                                children: 'Do you have a credit limit policy on your product? (if applicable)'
                            },
                            formControlLabel: {
                                disabled
                            },
                            radioGroup: {
                                'aria-labelledby': FormField.HasCreditLimitPolicy,
                                name: FormField.HasCreditLimitPolicy,
                                value: values[FormField.HasCreditLimitPolicy],
                                onChange: handleChange,
                                onBlur: handleBlur
                            },
                            textField: {
                                ...getFormInputProps(FormField.CreditLimitPolicyInput, form),
                                disabled,
                                name: FormField.CreditLimitPolicyInput,
                                value: values[FormField.CreditLimitPolicyInput],
                                label: 'IF YES: Provide Details',
                                onChange: handleChange,
                                onBlur: handleBlur
                            }
                        }}
                    />
                )}
            />
            <Typography
                marginTop={2}
                variant='h6'
            >
                List all fraud tools used
            </Typography>
            <OnboardingInputControl
                name={FormField.FraudToolsInput}
                isIncomplete={isIncomplete}
                requestedChangeFields={requestedChangeFields}
                renderInput={({ disabled }) => (
                    <TextField
                        {...getFormInputProps(FormField.FraudToolsInput, form)}
                        required
                        multiline
                        fullWidth
                        disabled={disabled}
                        rows={4}
                        size='small'
                        name={FormField.FraudToolsInput}
                        value={values[FormField.FraudToolsInput]}
                        label='Comma separated list of fraud tools used'
                        onChange={handleChange}
                        onBlur={handleBlur}
                    />
                )}
            />
            <OnboardingInputControl
                name={FormField.WasTerminatedByAcquirer}
                isIncomplete={isIncomplete}
                requestedChangeFields={requestedChangeFields}
                renderInput={({ disabled }) => (
                    <InputableRadioGroup
                        slotProps={{
                            typography: {
                                children: 'Have you ever been terminated by an Acquirer?'
                            },
                            formControlLabel: {
                                disabled
                            },
                            radioGroup: {
                                'aria-labelledby': FormField.WasTerminatedByAcquirer,
                                name: FormField.WasTerminatedByAcquirer,
                                value: values[FormField.WasTerminatedByAcquirer],
                                onChange: handleChange,
                                onBlur: handleBlur
                            },
                            textField: {
                                ...getFormInputProps(FormField.TerminatedByAcquirerInput, form),
                                disabled,
                                name: FormField.TerminatedByAcquirerInput,
                                value: values[FormField.TerminatedByAcquirerInput],
                                label: 'IF YES: Quote the name & explain the reason',
                                onChange: handleChange,
                                onBlur: handleBlur
                            }
                        }}
                    />
                )}
            />
            <OnboardingInputControl
                name={FormField.DoesCollectKyc}
                isIncomplete={isIncomplete}
                requestedChangeFields={requestedChangeFields}
                renderInput={({ disabled }) => (
                    <InputableRadioGroup
                        slots={{
                            textField: <></>
                        }}
                        slotProps={{
                            typography: {
                                sx: {
                                    mt: 2
                                },
                                children: 'Do you collect customer KYC?'
                            },
                            formControlLabel: {
                                disabled
                            },
                            radioGroup: {
                                'aria-labelledby': FormField.DoesCollectKyc,
                                name: FormField.DoesCollectKyc,
                                value: values[FormField.DoesCollectKyc],
                                onChange: handleChange,
                                onBlur: handleBlur
                            }
                        }}
                    />
                )}
            />
            <OnboardingInputControl
                name={FormField.DoesCollectKycViaOther}
                isIncomplete={isIncomplete}
                requestedChangeFields={requestedChangeFields}
                renderInput={({ disabled }) => (
                    <InputableRadioGroup
                        slotProps={{
                            typography: {
                                children: 'Is this done manually or using a 3rd party tool?'
                            },
                            formControlLabel: {
                                disabled
                            },
                            radioGroup: {
                                'aria-labelledby': FormField.DoesCollectKycViaOther,
                                name: FormField.DoesCollectKycViaOther,
                                value: values[FormField.DoesCollectKycViaOther],
                                onChange: handleChange,
                                onBlur: handleBlur
                            },
                            textField: {
                                ...getFormInputProps(FormField.CollectKycViaOtherInput, form),
                                disabled,
                                name: FormField.CollectKycViaOtherInput,
                                value: values[FormField.CollectKycViaOtherInput],
                                label: 'IF YES: Which provider do you use for this?',
                                onChange: handleChange,
                                onBlur: handleBlur
                            }
                        }}
                    />
                )}
            />
            <OnboardingInputControl
                name={FormField.HasFullTimeCustomerSupport}
                isIncomplete={isIncomplete}
                requestedChangeFields={requestedChangeFields}
                renderInput={({ disabled }) => (
                    <InputableRadioGroup
                        textInputRequiredWhen={BooleanSwitchValue.Off}
                        slotProps={{
                            typography: {
                                children: 'Do you have 24/7 customer support?'
                            },
                            formControlLabel: {
                                disabled
                            },
                            radioGroup: {
                                'aria-labelledby': FormField.HasFullTimeCustomerSupport,
                                name: FormField.HasFullTimeCustomerSupport,
                                value: values[FormField.HasFullTimeCustomerSupport],
                                onChange: handleChange,
                                onBlur: handleBlur
                            },
                            textField: {
                                ...getFormInputProps(FormField.FullTimeCustomerSupportInput, form),
                                disabled,
                                name: FormField.FullTimeCustomerSupportInput,
                                value: values[FormField.FullTimeCustomerSupportInput],
                                label: 'IF NO: Provide operational hours (e.g., “Mon–Sun 08:00–20:00 CET”)',
                                onChange: handleChange,
                                onBlur: handleBlur
                            }
                        }}
                    />
                )}
            />
            <Typography
                marginTop={2}
                variant='h6'
            >
                Provide your customer support contact email
            </Typography>
            <OnboardingInputControl
                name={FormField.CustomerSupportEmail}
                isIncomplete={isIncomplete}
                requestedChangeFields={requestedChangeFields}
                renderInput={({ disabled }) => (
                    <TextField
                        {...getFormInputProps(FormField.CustomerSupportEmail, form)}
                        fullWidth
                        required
                        type='email'
                        disabled={disabled}
                        size='small'
                        name={FormField.CustomerSupportEmail}
                        value={values[FormField.CustomerSupportEmail]}
                        label='Customer Support Email'
                        onChange={handleChange}
                        onBlur={handleBlur}
                    />
                )}
            />
            <Typography
                marginTop={2}
                variant='h6'
            >
                What are your refund & chargeback policies?
            </Typography>
            <OnboardingInputControl
                name={FormField.RefundChargebackPolicies}
                isIncomplete={isIncomplete}
                requestedChangeFields={requestedChangeFields}
                renderInput={({ disabled }) => (
                    <TextFieldLoadable
                        {...getFormInputProps(FormField.RefundChargebackPolicies, form)}
                        fullWidth
                        multiline
                        rows={4}
                        disabled={disabled}
                        size='small'
                        name={FormField.RefundChargebackPolicies}
                        value={values[FormField.RefundChargebackPolicies]}
                        label='Refund & Chargeback Policies'
                        onChange={handleChange}
                        onBlur={handleBlur}
                    />
                )}
            />
            <RefundChargebackPolicyFileUploader
                {...props}
            />
            <Typography
                marginTop={2}
                variant='h6'
            >
                Describe briefly the security features & risk management measures you will adopt
                <Box
                    component='span'
                    sx={{
                        fontStyle: 'italic',
                        fontWeight: 'normal'
                    }}
                >
                    - e.g. Proprietary or Third Party fraud scrubbing.
                </Box>
            </Typography>
            <OnboardingInputControl
                name={FormField.SecurityRiskManagementInput}
                isIncomplete={isIncomplete}
                requestedChangeFields={requestedChangeFields}
                renderInput={({ disabled }) => (
                    <TextFieldLoadable
                        {...getFormInputProps(FormField.SecurityRiskManagementInput, form)}
                        fullWidth
                        required
                        multiline
                        rows={4}
                        disabled={disabled}
                        size='small'
                        name={FormField.SecurityRiskManagementInput}
                        value={values[FormField.SecurityRiskManagementInput]}
                        label='Security Features & Risk Management Measures'
                        onChange={handleChange}
                        onBlur={handleBlur}
                    />
                )}
            />
            <Typography
                marginTop={2}
                variant='h6'
            >
                Are there any other risks you believe are worth mentioning for your business?
                <Box
                    component='span'
                    sx={{
                        fontStyle: 'italic',
                        fontWeight: 'normal'
                    }}
                >
                    (Optional)
                </Box>
            </Typography>
            <OnboardingInputControl
                name={FormField.OtherSecurityRiskInput}
                isIncomplete={isIncomplete}
                requestedChangeFields={requestedChangeFields}
                renderInput={({ disabled }) => (
                    <TextFieldLoadable
                        {...getFormInputProps(FormField.OtherSecurityRiskInput, form)}
                        fullWidth
                        multiline
                        rows={4}
                        disabled={disabled}
                        size='small'
                        name={FormField.OtherSecurityRiskInput}
                        value={values[FormField.OtherSecurityRiskInput]}
                        label='IF YES: Please provide details'
                        onChange={handleChange}
                        onBlur={handleBlur}
                    />
                )}
            />
        </OnboardingForm>
    );
};

export default MarketingWebsiteAndSupport;
